import {
  MOVEMENT_KIND_LABEL,
  formatMoney,
  type AvailabilityRow,
  type Lot,
  type LotTrace,
  type Page,
  type StockBalance,
  type StockMovement,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch } from "@/lib/api";
import { Pager, SearchInput } from "../cadastros/widgets";
import { dateBR, dateTimeBR, qty, unitCost } from "./lib";
import { WarehouseSelect } from "./pickers";

/** Saldos por produto, depósito e lote, com alerta de validade. */
export function SaldosPage() {
  const [q, setQ] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [offset, setOffset] = useState(0);
  const params = new URLSearchParams({ limit: "100", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  if (warehouseId) params.set("warehouseId", warehouseId);
  const list = useQuery({
    queryKey: ["estoque", "saldos", params.toString()],
    queryFn: () => apiFetch<Page<StockBalance>>(`/estoque/saldos?${params.toString()}`),
    placeholderData: (p) => p,
  });
  const check = useQuery({
    queryKey: ["estoque", "conferencia"],
    queryFn: () => apiFetch<{ ok: boolean; movements: number }>("/estoque/conferencia"),
  });
  const days = (d: string | null) =>
    d ? Math.round((new Date(`${d}T12:00`).getTime() - Date.now()) / 86_400_000) : null;

  return (
    <div className="space-y-6">
      <PageTitle
        title="Saldos de estoque"
        description="Saldo atual por produto, depósito e lote, com o custo médio, e o disponível para venda (descontadas as reservas dos pedidos aprovados). Só muda por movimentos: compras, vendas, produção, ajuste, inventário, cargas e transferências."
      />
      {check.data && !check.data.ok && (
        <Alert tone="error">
          A conferência encontrou saldo diferente da soma dos movimentos. Avise o suporte.
        </Alert>
      )}
      <Card>
        <div className="mb-4 flex flex-wrap gap-3">
          <SearchInput
            placeholder="Buscar produto ou lote"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <div className="w-60">
            <WarehouseSelect
              value={warehouseId}
              includeInactive
              allOption="Todos os depósitos"
              onChange={(v) => {
                setWarehouseId(v);
                setOffset(0);
              }}
            />
          </div>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={["Produto", "Depósito", "Lote", "Validade", "Saldo", "Custo médio", "Valor"]}
            >
              {list.data?.items.map((b) => {
                const d = days(b.expiresOn);
                return (
                  <tr key={`${b.warehouseId}-${b.productId}-${b.lotId ?? ""}`}>
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {b.productCode}
                      </span>{" "}
                      {b.productName}
                    </td>
                    <td className="px-3 py-2">{b.warehouseName}</td>
                    <td className="px-3 py-2 font-mono">{b.lotCode ?? "—"}</td>
                    <td className="px-3 py-2">
                      {dateBR(b.expiresOn)}{" "}
                      {d !== null && d < 0 && <Badge tone="error">vencido</Badge>}
                      {d !== null && d >= 0 && d <= 30 && (
                        <Badge tone="warn">vence em {d} dias</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {qty(b.quantity, b.baseUnitCode)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {b.averageCost ? `${unitCost(b.averageCost)}/${b.baseUnitCode}` : "—"}
                      {b.costStatus === "ausente" && (
                        <span className="ml-1">
                          <Badge tone="warn">sem custo</Badge>
                        </span>
                      )}
                      {b.costStatus === "provisorio" && (
                        <span
                          className="ml-1"
                          title="Há venda sem estoque a regularizar: o custo dessa saída só é apurado na regularização."
                        >
                          <Badge tone="warn">custo provisório</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatMoney(b.totalValue)}
                    </td>
                  </tr>
                );
              })}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} limit={100} onOffset={setOffset} />
          </>
        )}
      </Card>
      {/* nova busca/depósito: volta à primeira página */}
      <AvailabilityCard key={`${q}|${warehouseId}`} q={q} warehouseId={warehouseId} />
    </div>
  );
}

/** Disponível para venda por produto e depósito: saldo − vencido − reservado (pedidos aprovados). */
function AvailabilityCard({ q, warehouseId }: { q: string; warehouseId: string }) {
  const [offset, setOffset] = useState(0);
  const [onlyReserved, setOnlyReserved] = useState(false);
  const params = new URLSearchParams({ limit: "100", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  if (warehouseId) params.set("warehouseId", warehouseId);
  if (onlyReserved) params.set("reservados", "1");
  const list = useQuery({
    queryKey: ["estoque", "disponivel", params.toString()],
    queryFn: () => apiFetch<Page<AvailabilityRow>>(`/estoque/disponivel?${params.toString()}`),
    placeholderData: (p) => p,
  });
  return (
    <Card title="Disponível para venda">
      <p className="mb-3 text-sm text-muted-foreground">
        Por produto e depósito. Lote vencido não conta como vendável; o reservado é o dos pedidos de
        venda aprovados (ainda não confirmados). "A regularizar" é o que já foi vendido sem estoque
        (Estoque → Pendências de estoque); o saldo projetado (saldo − a regularizar) fica negativo
        enquanto a regularização não é feita.
      </p>
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={onlyReserved}
          onChange={(e) => {
            setOnlyReserved(e.target.checked);
            setOffset(0);
          }}
        />
        Só produtos com reserva
      </label>
      {list.isPending ? (
        <Spinner />
      ) : (
        <>
          <Table
            head={[
              "Produto",
              "Depósito",
              "Saldo",
              "Vencido",
              "Reservado",
              "A regularizar",
              "Saldo projetado",
              "Disponível",
            ]}
          >
            {list.data?.items.map((r) => (
              <tr key={`${r.warehouseId}-${r.productId}`}>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs text-muted-foreground">{r.productCode}</span>{" "}
                  {r.productName}
                </td>
                <td className="px-3 py-2">{r.warehouseName}</td>
                <td className="px-3 py-2 text-right">{qty(r.balance, r.baseUnitCode)}</td>
                <td className="px-3 py-2 text-right">
                  {Number(r.expired) !== 0 ? qty(r.expired, r.baseUnitCode) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  {Number(r.reserved) !== 0 ? qty(r.reserved, r.baseUnitCode) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  {Number(r.pending) !== 0 ? qty(r.pending, r.baseUnitCode) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  {qty(r.projected, r.baseUnitCode)}{" "}
                  {Number(r.projected) < 0 && <Badge tone="error">negativo</Badge>}
                </td>
                <td className="px-3 py-2 text-right font-medium">
                  {qty(r.available, r.baseUnitCode)}{" "}
                  {Number(r.available) <= 0 && <Badge tone="warn">sem disponível</Badge>}
                </td>
              </tr>
            ))}
          </Table>
          <Pager total={list.data?.total ?? 0} offset={offset} limit={100} onOffset={setOffset} />
        </>
      )}
    </Card>
  );
}

/** Livro de movimentos (somente leitura). */
export function MovimentosPage() {
  const [warehouseId, setWarehouseId] = useState("");
  const [kind, setKind] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  const params = new URLSearchParams({ limit: "50", offset: String(offset) });
  if (warehouseId) params.set("warehouseId", warehouseId);
  if (kind) params.set("kind", kind);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const list = useQuery({
    queryKey: ["estoque", "movimentos", params.toString()],
    queryFn: () => apiFetch<Page<StockMovement>>(`/estoque/movimentos?${params.toString()}`),
    placeholderData: (p) => p,
  });
  const reset = () => setOffset(0);

  return (
    <div className="space-y-6">
      <PageTitle
        title="Movimentações"
        description="Livro de estoque: cada entrada e saída, com origem, usuário e horário. Nada aqui é alterado ou apagado; correções entram como estorno."
      />
      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <Field label="Depósito">
            <WarehouseSelect
              value={warehouseId}
              includeInactive
              allOption="Todos"
              onChange={(v) => {
                setWarehouseId(v);
                reset();
              }}
            />
          </Field>
          <Field label="Tipo">
            <Select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value);
                reset();
              }}
            >
              <option value="">Todos</option>
              {Object.entries(MOVEMENT_KIND_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="De">
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                reset();
              }}
            />
          </Field>
          <Field label="Até">
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                reset();
              }}
            />
          </Field>
        </div>
        {list.isPending ? <Spinner /> : <MovementsTable rows={list.data?.items ?? []} />}
        <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
      </Card>
    </div>
  );
}

export function MovementsTable({ rows }: { rows: StockMovement[] }) {
  return (
    <Table
      head={[
        "Quando",
        "Tipo",
        "Origem",
        "Produto",
        "Depósito",
        "Lote",
        "Quantidade",
        "Na base",
        "Custo unit.",
        "Usuário",
      ]}
    >
      {rows.map((m) => (
        <tr key={m.id} className={m.isReversal ? "text-muted-foreground" : undefined}>
          <td className="px-3 py-2 whitespace-nowrap">{dateTimeBR(m.occurredAt)}</td>
          <td className="px-3 py-2">
            {MOVEMENT_KIND_LABEL[m.kind] ?? m.kind}
            {m.isReversal && <Badge tone="error">estorno</Badge>}
          </td>
          <td className="px-3 py-2 font-mono text-xs">{m.sourceNumber ?? m.sourceType}</td>
          <td className="px-3 py-2">
            <span className="font-mono text-xs">{m.productCode}</span> {m.productName}
          </td>
          <td className="px-3 py-2">{m.warehouseName}</td>
          <td className="px-3 py-2 font-mono">{m.lotCode ?? "—"}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            {m.direction > 0 ? "+" : "−"}
            {qty(m.quantity, m.unitCode)}
          </td>
          <td
            className={`px-3 py-2 text-right font-medium whitespace-nowrap ${m.direction > 0 ? "text-success" : "text-destructive"}`}
          >
            {m.direction > 0 ? "+" : "−"}
            {qty(m.quantityBase.replace("-", ""), m.baseUnitCode)}
          </td>
          <td className="px-3 py-2 text-right whitespace-nowrap">{unitCost(m.unitCost)}</td>
          <td className="px-3 py-2 text-xs">{m.userEmail ?? "—"}</td>
        </tr>
      ))}
    </Table>
  );
}

/** Lotes com validade, saldo e rastreabilidade. */
export function LotesPage() {
  const [q, setQ] = useState("");
  const [withBalance, setWithBalance] = useState(true);
  const [offset, setOffset] = useState(0);
  const [tracing, setTracing] = useState<string | null>(null);
  const params = new URLSearchParams({
    limit: "100",
    offset: String(offset),
    comSaldo: withBalance ? "1" : "0",
  });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: ["estoque", "lotes", params.toString()],
    queryFn: () => apiFetch<Page<Lot>>(`/estoque/lotes?${params.toString()}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Lotes"
        description="Validade e saldo de cada lote. O rastreio mostra de quais lotes de insumo o produto foi feito e para onde foi."
      />
      {tracing && <TraceCard lotId={tracing} onClose={() => setTracing(null)} />}
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            placeholder="Buscar lote ou produto"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={withBalance}
              onChange={(e) => {
                setWithBalance(e.target.checked);
                setOffset(0);
              }}
            />
            Só com saldo
          </label>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Lote", "Produto", "Fabricação", "Validade", "Origem", "Saldo", ""]}>
              {list.data?.items.map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-2 font-mono">{l.code}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{l.productCode}</span> {l.productName}
                  </td>
                  <td className="px-3 py-2">{dateBR(l.manufacturedOn)}</td>
                  <td className="px-3 py-2">
                    {dateBR(l.expiresOn)}{" "}
                    {l.daysToExpire !== null && l.daysToExpire < 0 && (
                      <Badge tone="error">vencido</Badge>
                    )}
                    {l.daysToExpire !== null && l.daysToExpire >= 0 && l.daysToExpire <= 30 && (
                      <Badge tone="warn">{l.daysToExpire} dias</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">{l.origin}</td>
                  <td className="px-3 py-2 text-right">{qty(l.balance)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setTracing(l.id)}>
                      Rastrear
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} limit={100} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function TraceCard({ lotId, onClose }: { lotId: string; onClose: () => void }) {
  const trace = useQuery({
    queryKey: ["estoque", "lotes", lotId, "rastreio"],
    queryFn: () => apiFetch<LotTrace>(`/estoque/lotes/${lotId}/rastreio`),
  });
  const t = trace.data;
  return (
    <Card
      title={t ? `Rastreio do lote ${t.lot.code} — ${t.lot.productName}` : "Rastreio"}
      actions={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {!t ? (
        <Spinner />
      ) : (
        <div className="space-y-5 text-sm">
          <section>
            <h3 className="mb-1 font-semibold">De onde veio</h3>
            {t.origin ? (
              <>
                <p>
                  Produzido na ordem <span className="font-mono">{t.origin.number}</span> em{" "}
                  {dateBR(t.origin.productionDate)}, com:
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {t.origin.consumed.map((c, i) => (
                    <li key={i}>
                      {c.productName}{" "}
                      {c.lotCode ? <span className="font-mono">(lote {c.lotCode})</span> : ""}:{" "}
                      {qty(c.quantityBase, c.unitCode)}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-muted-foreground">
                Entrada por {t.lot.origin} (sem ordem de produção).
              </p>
            )}
          </section>
          {t.usedIn.length > 0 && (
            <section>
              <h3 className="mb-1 font-semibold">Usado na produção de</h3>
              <ul className="list-disc pl-5">
                {t.usedIn.map((u) => (
                  <li key={u.productionOrderId}>
                    Ordem <span className="font-mono">{u.number}</span> ({u.productCode}
                    {u.outputLotCode ? `, lote ${u.outputLotCode}` : ""}): {qty(u.quantityBase)}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h3 className="mb-1 font-semibold">Movimentos do lote</h3>
            <MovementsTable rows={t.movements} />
          </section>
        </div>
      )}
    </Card>
  );
}
